import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { StatusCodes } from 'http-status-codes';
import { ReactComponent as SideBySideIcon } from 'icons/compare.svg';
import Button, { BUTTON_TYPES } from 'components/common/button-component';
import SelectStatement from './_select-statement-tab';
import SelectRevisionTab from './_select-revision-tab';
import SelectLinkedStatementsTab from './_select-linked-statements';
import TrialBalanceImport from './trial-balance-import-view';
import { setSourceStatementParams } from 'store/actions/source-statement-actions';
import { setSourceStatementLoaded } from 'store/actions/load-source-statement-actions';
import { resetSideBySideViewMode } from 'store/actions/side-by-side-view-annotations-actions';
import {
  clearLinkedStatements,
  getLinkedStatementsAction,
} from 'store/actions/linked-statement-list-actions';
import { connect } from 'react-redux';
import LeftSideStatementView from 'pages/side-by-side-compare/left-side/_left-side-statement-view';
import TrialBalanceSnapshotView from 'pages/side-by-side-compare/left-side/trial-balance-snapshot-view';
import { TAB_NAME } from 'constants/util/side-by-side-utils';
import classNames from 'classnames';
import { updateSelectedProjectFromIDIfDoesNotExists } from 'store/actions/selected-project-actions';
import {
  setTrialBalanceSnapshotViewLoaded,
  setSnapshotReadOnly,
} from 'store/actions/trial-balance/trial-balance-snapshot-view-action';
import { RadioOption } from 'models/utils/common/radio-options-model';
import LeftSideRadioOption from './left-side-radio-component';
import {
  AUTO_TIEOUT_COMPARE_MODE,
  SIDE_BY_SIDE_COMPARE_MODE,
  TRAIL_BALANCE_MODE,
} from 'constants/feature/statement-content-constants';
import RolesUtil from 'permissions/utils/roles';
import { getOmniaSnapshotDetails } from 'api/statement-api';
import { getTrailBalanceWorkFlowRequest } from 'api/trial-balance-api';
import Loading from 'components/common/loading-component';
import {
  clearBatchSelectedElementsAction,
  clearSelectedElementsContentPanel,
  clearSideBySideElementsMap,
} from 'store/actions/action-creators';
import { resetThresholdScaling } from 'store/actions/trial-balance/trial-balance-action';
import {
  removeTBSourceElementClicked,
  removeTBTargetElementClicked,
} from 'store/actions/trial-balance/mapped-element-action';
import { cancelBatchUpdatePanel } from 'store/actions/batch-panel-actions';
import { clearModeIfPossible } from 'store/actions/modes-actions';
import { ELEMENT_SELECT_MODES } from 'constants/feature/modes-constants';
import {
  setWorkpaperFilterReset,
  resetAnnotationSidebySideView,
} from 'store/actions/annotation-display-actions';

export const LEFT_PANEL_BLOCK = 'left-panel';
export const LEFT_PANEL_BLOCK_ID = 'left-panel-id';

const LeftSideView = ({
  setSourceStatementParams,
  isSourceStatementLoaded,
  setSourceStatementLoaded,
  isSwapped,
  onConfirmStatement,
  selectedTab,
  setSelectedTab,
  selectedProject,
  updateSelectedProjectFromIDIfDoesNotExists,
  setLeftStatementNavPanel,
  leftStatementNavPanel,
  isAutoTieoutCompareModeEnabled,
  isAutoTieoutToTrialBalanceEnabled,
  sideBySideViewMode,
  sideBySideViewModeAction,
  linkedStatements,
  clearLinkedStatements,
  getLinkedStatementsAction,
  resetSideBySideViewMode,
  isSnapshotViewLoaded,
  setTrialBalanceSnapshotViewLoaded,
  setSnapshotReadOnly,
  isSnapshotReadOnly,
  projectId,
  statementId,
  revisionId,
  stateData,
  selectedStatement,
  clearBatchSelectedElementsAction,
  clearSelectedElementsContentPanel,
  resetThresholdScaling,
  clearSideBySideElementsMap,
  removeTBSourceElementClicked,
  removeTBTargetElementClicked,
  batchElementList,
  cancelBatchUpdatePanel,
  clearModeIfPossible,
  selectMode,
  resetAnnotationSidebySideView,
  setWorkpaperFilterReset,
  editPermissionAutotieout,
}) => {
  const [disabled, setDisabled] = useState(true);
  const [isPending, startTransition] = useTransition();
  const [trialBalanceData, setTrialBalanceData] = useState({});
  const [disableConfirmButton, setDisableConfirmButton] = useState(false);
  const noClass = 'undefined';
  const leftActive =
    selectedTab === TAB_NAME.LEFT ? `${LEFT_PANEL_BLOCK}__active` : noClass;
  const rightActive =
    selectedTab === TAB_NAME.RIGHT ? `${LEFT_PANEL_BLOCK}__active` : noClass;
  const isButtonDisabled = (isDisabled) => setDisabled(isDisabled);
  const [sourceStatementParamsState, setSourceStatementParamsState] = useState(
    {},
  );
  const [isTieoutToTBDisabled, setIsTieoutToTBDisabled] = useState(true);

  const isSideBySideMode =
    sideBySideViewMode.viewMode === SIDE_BY_SIDE_COMPARE_MODE;
  const isAutoTieoutMode =
    sideBySideViewMode.viewMode === AUTO_TIEOUT_COMPARE_MODE;
  const isTrialBalanceMode = sideBySideViewMode.viewMode === TRAIL_BALANCE_MODE;

  // Use refs to avoid stale closures in useCallback/useMemo
  const selectModeRef = useRef(selectMode);
  selectModeRef.current = selectMode;
  const batchElementListRef = useRef(batchElementList);
  batchElementListRef.current = batchElementList;

  const resetData = () => {
    resetAnnotationSidebySideView();
    setWorkpaperFilterReset(true);
    cancelBatchUpdatePanel();
    clearModeIfPossible();
    clearSelectedElementsContentPanel();
    clearBatchSelectedElementsAction();
  };

  // getting the data of target/current statement is in wrapped up or pending approval state from selected statement model in store, as this function is checking both conditions (isWrappedUp() and isPendingApproval()) in one function only, instead of calling two different functions in model for checking two different conditions
  const isStatementWrappedOrPendingApproval =
    selectedStatement.isStatementWrappedOrPendingApproval();

  // Condition for disabling auto-tieout features for observer users
  const isAutoTieoutDisabledForObserver =
    (isAutoTieoutMode || isTrialBalanceMode) &&
    RolesUtil.doesUserHaveObserverRoleForProject(stateData, projectId);

  const onSelectTab = (tabname) => {
    if (tabname === selectedTab) return;
    setSelectedTab(tabname);
    isButtonDisabled(true);
  };

  const setSourceStatementParamsValues = (sourceStatementParams) => {
    setSourceStatementParamsState(sourceStatementParams);
    setSourceStatementParams(sourceStatementParams);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearLinkedStatements();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // This useEffect is to check if the project is linked to OMNIA then enabling tieout to trial balance option.
    // If not linked to OMNIA then we will have 2 cases, need to check the workflow has data or not.
    // If workflow has data it means we have existing trial balance auto tieout data, then enabling tieout to trial balance option.
    // If workflow does not have data then it means we don't have existing trial balance auto tieout data, then disabling tieout to trial balance option.
    const checkWorkflowData = async () => {
      try {
        const response = await getTrailBalanceWorkFlowRequest({
          clientId: parseInt(projectId),
          statementId,
          revisionId,
        });
        setIsTieoutToTBDisabled(!response?.data);
      } catch (error) {
        console.error('Error checking workflow data:', error);
        setIsTieoutToTBDisabled(true);
      }
    };

    if (!selectedProject?.id) {
      setIsTieoutToTBDisabled(true);
      return;
    }

    if (selectedProject?.hasProjectLinkedToOmniaEngagementEntity) {
      setIsTieoutToTBDisabled(false);
    } else {
      checkWorkflowData();
    }
  }, [selectedProject, projectId, statementId, revisionId]);

  const fetchTrialBalanceData = async () => {
    try {
      return await getOmniaSnapshotDetails({
        clientId: parseInt(projectId),
        statementId,
        revisionId,
      });
    } catch (error) {
      console.error('Error fetching trial balance data:', error);
      return null;
    }
  };

  const getOmniaSnapshotData = async () => {
    const response = await fetchTrialBalanceData();
    if (response?.status === StatusCodes.OK) {
      setTrialBalanceSnapshotViewLoaded(true);
      setSourceStatementLoaded(true);
      setTrialBalanceData(response?.data);
      setSnapshotReadOnly(response?.data?.readonly);
    }
  };

  const onClearTrialBalanceData = () => {
    setTrialBalanceSnapshotViewLoaded(false);
    setSourceStatementLoaded(false);
    setTrialBalanceData({});
    resetSideBySideViewMode();
    resetData();
    resetThresholdScaling();
    clearSideBySideElementsMap();
    removeTBSourceElementClicked();
    removeTBTargetElementClicked();
  };

  const onSelectTrialBalanceMode = useCallback(() => {
    resetData();
    sideBySideViewModeAction(TRAIL_BALANCE_MODE);
    clearLinkedStatements();
    startTransition(async () => {
      const response = await fetchTrialBalanceData();
      if (response?.status === StatusCodes.OK) {
        setTrialBalanceSnapshotViewLoaded(true);
        setSourceStatementLoaded(true);
        setTrialBalanceData(response?.data);
        setSnapshotReadOnly(response?.data?.readonly);
      } else if (response?.status === StatusCodes.NO_CONTENT) {
        setTrialBalanceSnapshotViewLoaded(false);
        setSourceStatementLoaded(false);
      }
    });
  }, [projectId, statementId, revisionId]);

  const isCurrentStatementWrappedUp = selectedStatement.isInWrappedUpFlow();

  const trialBalanceView = useMemo(() => {
    if (!isSnapshotViewLoaded) {
      return isPending ? (
        <Loading />
      ) : (
        <TrialBalanceImport
          getOmniaSnapshotData={getOmniaSnapshotData}
          isDisabledImportButton={
            isAutoTieoutDisabledForObserver ||
            isCurrentStatementWrappedUp ||
            !editPermissionAutotieout
          }
        />
      );
    }
    return null;
  }, [
    isPending,
    isSnapshotViewLoaded,
    isAutoTieoutDisabledForObserver,
    isCurrentStatementWrappedUp,
  ]);

  const leftSideView = useMemo(() => {
    return sideBySideViewMode.viewMode === TRAIL_BALANCE_MODE &&
      isSnapshotViewLoaded ? (
      <TrialBalanceSnapshotView
        isSwapped={isSwapped}
        trialBalanceData={trialBalanceData}
        setTrialBalanceData={setTrialBalanceData}
        fetchTrialBalanceData={fetchTrialBalanceData}
        onCloseTab={onClearTrialBalanceData}
        // Disabling snapshot view if user is observer or statement is wrapped up or snapshot is read-only,
        // as all conditions are not allowing user to use auto-tieout features (map/unmap)
        isSnapshotReadOnly={
          isSnapshotReadOnly ||
          isAutoTieoutDisabledForObserver ||
          isCurrentStatementWrappedUp
        }
      />
    ) : (
      <LeftSideStatementView
        isSourceStatementLoaded={isSourceStatementLoaded}
        isSwapped={isSwapped}
        onConfirmStatement={onConfirmStatement}
        sideBySideViewMode={sideBySideViewMode}
        setLeftStatementNavPanel={setLeftStatementNavPanel}
        leftStatementNavPanel={leftStatementNavPanel}
      />
    );
  }, [
    isPending,
    isSourceStatementLoaded,
    sideBySideViewMode.viewMode,
    isSnapshotViewLoaded,
    isSwapped,
    leftStatementNavPanel,
    trialBalanceData,
  ]);

  return (
    <div className={`${LEFT_PANEL_BLOCK} ${LEFT_PANEL_BLOCK}__split`}>
      {isSourceStatementLoaded ? (
        leftSideView
      ) : (
        <>
          <div className={`${LEFT_PANEL_BLOCK}__subheader`}></div>
          <div
            className={classNames(
              `${LEFT_PANEL_BLOCK}__wrap`,
              `${LEFT_PANEL_BLOCK}__container__left`,
              {
                [`${LEFT_PANEL_BLOCK}__auto-tieout-enabled`]:
                  isAutoTieoutCompareModeEnabled ||
                  isAutoTieoutToTrialBalanceEnabled,
              },
            )}
          >
            <div className={`${LEFT_PANEL_BLOCK}__compare__header`}>
              <FormattedMessage id="side-by-side-view.compare.radio-buttons.heading" />
            </div>

            <div className={`${LEFT_PANEL_BLOCK}__compare__options`}>
              {isAutoTieoutCompareModeEnabled && (
                <LeftSideRadioOption
                  id={`${LEFT_PANEL_BLOCK_ID}__auto-tieout-option`}
                  wrapperClassName={`${LEFT_PANEL_BLOCK}__auto-tieout`}
                  radioWrapperClass={`${LEFT_PANEL_BLOCK}__radio`}
                  className={`${LEFT_PANEL_BLOCK}__radio_wrap`}
                  options={[
                    new RadioOption({
                      id: 1,
                      label:
                        'side-by-side-view.compare.radio-button.auto-tieout-option',
                      value: AUTO_TIEOUT_COMPARE_MODE,
                      tooltipEnabled: true,
                    }),
                  ]}
                  selectedOption={sideBySideViewMode.viewMode}
                  onChange={() => {
                    sideBySideViewModeAction(AUTO_TIEOUT_COMPARE_MODE);
                    getLinkedStatementsAction(
                      projectId,
                      statementId,
                      revisionId,
                    );
                    resetData();
                  }}
                  showGuidanceMessage={!sideBySideViewMode.viewMode}
                  guidanceMessage="side-by-side-view.compare.radio-button.auto-tieout-radio-option.description"
                />
              )}
              {isAutoTieoutToTrialBalanceEnabled && (
                <LeftSideRadioOption
                  id={`${LEFT_PANEL_BLOCK_ID}__side-by-side-mapping`}
                  wrapperClassName={`${LEFT_PANEL_BLOCK}__side-by-side`}
                  tooltip={{
                    text: 'side-by-side-view.compare.radio-button.auto-tieout-trial-balance-disabled-option.description',
                  }}
                  options={[
                    new RadioOption({
                      id: 3,
                      label:
                        'side-by-side-view.compare.radio-button.auto-tieout-trial-balance-option',
                      value: TRAIL_BALANCE_MODE,
                      disabled: isTieoutToTBDisabled,
                      tooltipEnabled: isTieoutToTBDisabled,
                    }),
                  ]}
                  disabled={isTieoutToTBDisabled}
                  selectedOption={sideBySideViewMode.viewMode}
                  onChange={onSelectTrialBalanceMode}
                  showGuidanceMessage={!sideBySideViewMode.viewMode}
                  guidanceMessage="side-by-side-view.compare.radio-button.auto-tieout-trial-balance-option.description"
                />
              )}
              <LeftSideRadioOption
                id={`${LEFT_PANEL_BLOCK_ID}__side-by-side-mapping`}
                wrapperClassName={`${LEFT_PANEL_BLOCK}__side-by-side`}
                options={[
                  new RadioOption({
                    id: 2,
                    label:
                      'side-by-side-view.compare.radio-button.side-by-side-option',
                    value: SIDE_BY_SIDE_COMPARE_MODE,
                    disabled: isCurrentStatementWrappedUp,
                    tooltipEnabled: true,
                  }),
                ]}
                selectedOption={sideBySideViewMode.viewMode}
                onChange={() => {
                  sideBySideViewModeAction(SIDE_BY_SIDE_COMPARE_MODE);
                  clearLinkedStatements();
                  resetData();
                }}
                disabled={isCurrentStatementWrappedUp}
                showGuidanceMessage={!sideBySideViewMode.viewMode}
                guidanceMessage="side-by-side-view.compare.radio-button.side-by-side-option.description"
              />
            </div>
            {isAutoTieoutMode || isSideBySideMode ? (
              <>
                <div className={`${LEFT_PANEL_BLOCK}__section`}>
                  <div className={`${LEFT_PANEL_BLOCK}__icon`}>
                    <SideBySideIcon width={30} height={30} />
                  </div>
                  <div className={`${LEFT_PANEL_BLOCK}__title`}>
                    {isAutoTieoutMode ? (
                      <FormattedMessage id="side-by-side-view.compare.left-panel.auto-tieout.title" />
                    ) : (
                      <FormattedMessage id="side-by-side-view.compare.left-panel.title" />
                    )}
                  </div>
                  {selectedTab === TAB_NAME.RIGHT && (
                    <p className={`${LEFT_PANEL_BLOCK}__subtitle`}>
                      <FormattedMessage id="side-by-side-view.compare.left-panel.subtitle" />
                    </p>
                  )}
                  <div className={`${LEFT_PANEL_BLOCK}__tab`}>
                    <div
                      className={`${LEFT_PANEL_BLOCK}__left-tab ${leftActive}`}
                      onClick={() => onSelectTab(TAB_NAME.LEFT)}
                    >
                      {sideBySideViewMode.viewMode ===
                      AUTO_TIEOUT_COMPARE_MODE ? (
                        <FormattedMessage id="side-by-side-view.compare.left-panel.auto-tieout.left-tab" />
                      ) : (
                        <FormattedMessage id="side-by-side-view.compare.left-panel.left-tab" />
                      )}
                    </div>
                    <div
                      className={`${LEFT_PANEL_BLOCK}__right-tab ${rightActive}`}
                      onClick={() => onSelectTab(TAB_NAME.RIGHT)}
                    >
                      <FormattedMessage id="side-by-side-view.compare.left-panel.right-tab" />
                    </div>
                  </div>
                </div>
                <div
                  className={classNames({
                    [`${LEFT_PANEL_BLOCK}__section_tab_data`]:
                      isSideBySideMode ||
                      (isAutoTieoutMode && selectedTab === TAB_NAME.RIGHT),
                    [`${LEFT_PANEL_BLOCK}__section_tab_linked_statement_data`]:
                      isAutoTieoutMode && selectedTab === TAB_NAME.LEFT,
                  })}
                >
                  {selectedTab === TAB_NAME.RIGHT ? (
                    <SelectStatement
                      selectedProjectData={selectedProject}
                      isButtonDisabled={isButtonDisabled}
                      setSourceStatementParamsValues={
                        setSourceStatementParamsValues
                      }
                      sideBySideViewMode={sideBySideViewMode}
                      linkedStatements={linkedStatements}
                      isRadiobuttonDisabled={isAutoTieoutDisabledForObserver}
                      // target/current statement is in wrapped or pending approval state, then disabling confirm button and showing tooltip message in statement selection radio button
                      isStatementWrappedOrPendingApproval={
                        isStatementWrappedOrPendingApproval
                      }
                      setDisableConfirmButton={setDisableConfirmButton}
                      isTrailBalanceMode={isTrialBalanceMode}
                      isAutoTieoutCompareMode={isAutoTieoutMode}
                      editPermissionAutotieout={editPermissionAutotieout}
                    />
                  ) : isAutoTieoutMode ? (
                    <SelectLinkedStatementsTab
                      isButtonDisabled={isButtonDisabled}
                      onConfirmStatement={onConfirmStatement}
                      sourceStatementParams={sourceStatementParamsState}
                      setSourceStatementParamsValues={
                        setSourceStatementParamsValues
                      }
                      isDisableUnlink={RolesUtil.doesUserHaveObserverRoleForProject(
                        stateData,
                        projectId,
                      )}
                      isCurrentStatementWrappedUp={isCurrentStatementWrappedUp}
                      currentUserId={stateData.currentUser.id}
                    />
                  ) : (
                    <SelectRevisionTab
                      isButtonDisabled={isButtonDisabled}
                      setSourceStatementParamsValues={
                        setSourceStatementParamsValues
                      }
                      updateSelectedProjectFromIDIfDoesNotExists={
                        updateSelectedProjectFromIDIfDoesNotExists
                      }
                    />
                  )}
                  {!(isAutoTieoutMode && selectedTab === TAB_NAME.LEFT) && (
                    <Button
                      id={`${LEFT_PANEL_BLOCK_ID}-save`}
                      className={`${LEFT_PANEL_BLOCK}__save`}
                      onClick={onConfirmStatement}
                      type={BUTTON_TYPES.primary}
                      disabled={
                        disabled ||
                        isAutoTieoutDisabledForObserver ||
                        ((isAutoTieoutMode || isTrialBalanceMode) &&
                          (disableConfirmButton || !editPermissionAutotieout))
                      }
                    >
                      <FormattedMessage id="side-by-side-view.compare.button.confirm" />
                    </Button>
                  )}
                </div>
              </>
            ) : null}
            {isTrialBalanceMode && <>{trialBalanceView}</>}
          </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  const {
    data,
    data: {
      selectedProject,
      batchPanel: { batchElementList },
      editPermission: { editPermissionAutotieout },
    },
    ui: {
      isSourceStatementLoaded,
      sideBySideView: {
        autoTieout: { linkedStatements },
        trialBalance: { isSnapshotViewLoaded, isSnapshotReadOnly },
      },
      statementPage: {
        modes: { selectMode },
      },
    },
  } = state;

  return {
    selectedProject: selectedProject.project,
    linkedStatements,
    isSnapshotViewLoaded,
    isSnapshotReadOnly,
    stateData: data,
    isSourceStatementLoaded,
    batchElementList,
    selectMode,
    editPermissionAutotieout,
  };
};

const mapDispatchToProps = {
  setSourceStatementParams,
  clearLinkedStatements,
  getLinkedStatementsAction,
  updateSelectedProjectFromIDIfDoesNotExists,
  resetSideBySideViewMode,
  setTrialBalanceSnapshotViewLoaded,
  setSnapshotReadOnly,
  clearBatchSelectedElementsAction,
  clearSelectedElementsContentPanel,
  resetThresholdScaling,
  setSourceStatementLoaded,
  clearSideBySideElementsMap,
  removeTBSourceElementClicked,
  removeTBTargetElementClicked,
  cancelBatchUpdatePanel,
  clearModeIfPossible,
  resetAnnotationSidebySideView,
  setWorkpaperFilterReset,
};

export default connect(mapStateToProps, mapDispatchToProps)(LeftSideView);
